/**
 * THE gull, in the shared 120×120 LINE asset box.
 *
 * Two themes draw this bird and there is exactly ONE drawing of it:
 *  - Roraima's chat wallpaper drifts a flock (BIRDS / BIRD_COLORS),
 *  - Istanbul's passcode dots are a single chunky one (GULL_PASSCODE) — the
 *    owner made the martı Istanbul's motif on 2026-08-14.
 * They differ only in the numbers handed to `gullD`. Forking a second gull for
 * the passcode is exactly what would let the two silhouettes drift apart, so
 * the parameters moved into the shared builder instead. `birdD` keeps the
 * wallpaper's own numbers as its defaults, so every path it has ever produced
 * is byte-for-byte what it produced before.
 *
 * The wallpaper's story below is unchanged, and still explains the shape.
 *
 * The chat wallpaper's fragment (owner 2026-08-13: "kuşlar iyi fikir roraima
 * renklerinde"). Two motifs were built and thrown out before it: the theme's own
 * tepui scattered at every scale, which is a mountain hanging in mid-air ("uçan
 * dağlar saçma bir fikir"), and moriche fronds, which drift honestly enough but
 * were not of this place.
 *
 * A bird solves the thing both of those got wrong. The wallpaper needs something
 * that is genuinely IN the air — that is the whole lesson of the flying
 * mountains — and a bird over the savanna at sunset is the one subject here that
 * is neither borrowed from another continent nor pinned to the ground.
 *
 * Drawn as the classic two-arc gull: it is legible at any size down to a few
 * pixels, which a wallpaper motif has to be, and it needs no interior detail to
 * be read. Flat, like everything else in this family.
 */

const f = ( v: number ) => +v.toFixed( 1 );

/**
 * A tapered blade along a CUBIC spine: thick at the root, a fine point at the
 * tip. Cubic rather than quadratic because a gull's wing has a crank in it —
 * see birdD — and one control point cannot bend a curve twice.
 */
function blade( p0: [ number, number ], c1: [ number, number ], c2: [ number, number ],
                p1: [ number, number ], rootW: number, N = 24, taper = 1.15 ): string {
  const at = ( t: number ): [ number, number ] => {
    const u = 1 - t;
    return [
      u * u * u * p0[ 0 ] + 3 * u * u * t * c1[ 0 ] + 3 * u * t * t * c2[ 0 ] + t * t * t * p1[ 0 ],
      u * u * u * p0[ 1 ] + 3 * u * u * t * c1[ 1 ] + 3 * u * t * t * c2[ 1 ] + t * t * t * p1[ 1 ],
    ];
  };
  const top: string[] = [];
  const bot: string[] = [];
  for ( let i = 0; i <= N; i++ ) {
    const t = i / N;
    const [ x, y ] = at( t );
    const [ nx, ny ] = at( Math.min( 1, t + 0.008 ) );
    let dx = nx - x, dy = ny - y;
    const L = Math.hypot( dx, dy ) || 1;
    dx /= L; dy /= L;
    // Tapers at BOTH ends. A flat cap at the root left a step under the join
    // where the two wings met (owner 2026-08-13: "kanatlarının birleştiği alt
    // kısım kesik olmasın"); tapered roots crossing the centre line union into
    // one continuous underside instead.
    // `taper` is how fast the blade gives its width up on the way to the tip.
    // 1.15 is the flock's, and it is what both callers use: the passcode ran a
    // slower one for a while to hold ink at the tips down at 22px, and the
    // owner sent it back — a bird that heavy stopped being the same bird.
    const w = rootW * Math.pow( 1 - t, taper ) * ( 0.30 + 1.0 * Math.sin( Math.PI * Math.pow( t, 0.45 ) ) );
    top.push( `${ f( x - dy * w ) },${ f( y + dx * w ) }` );
    bot.push( `${ f( x + dy * w ) },${ f( y - dx * w ) }` );
  }
  return `M${ top[ 0 ] } L${ top.slice( 1 ).join( ' L' ) } L${ bot.reverse().join( ' L' ) } Z`;
}

/** Every dial on the bird. Each one defaults to the wallpaper's own value, so
 *  `gullD( { lift } )` is the flock's bird and nothing else has to be said. */
export interface GullShape {
  /** Where it is in its wing beat (0 = a low glide, 1 = wings high). */
  lift?: number;
  /** How high the wings ride, set straight. Overrides `lift`. */
  rise?: number;
  /** Lengthens and lifts the RIGHT wing. */
  asym?: number;
  /** Half-width of a wing at its root — the whole bird's weight. */
  rootW?: number;
  /** How fast the wing gives that width up toward the tip (see `blade`). */
  taper?: number;
  /** The wing's reach from centre. */
  span?: number;
  /** The two control points' distance out, which is where the wrist cranks. */
  c1x?: number;
  c2x?: number;
  /** How far PAST centre each wing root starts, so the two undersides merge. */
  rootIn?: number;
  /** Where the tip lands, as a fraction of `rise`. Under 1 = it comes back
   *  down, which is the crank that separates a gull from a bat. */
  tipF?: number;
  /** The body: half-length, and how far it bulges below / above the wing line. */
  bodyW?: number;
  belly?: number;
  back?: number;
  /** Where the bird sits in the 120 box. */
  cy?: number;
}

/**
 * One bird: two long wings meeting over a slim body.
 *
 * `lift` is where it is in its wing beat (0 = a low glide, 1 = wings high).
 * `asym` lengthens and lifts the RIGHT wing, because a bird caught mid-air is
 * almost never symmetrical and a mirrored one reads as a logo.
 *
 * WHAT MAKES IT A GULL AND NOT A BAT (owner 2026-08-13: "yarasa gibi yaptın
 * şuanda martıları"), three things, and the first pass missed all three:
 *  - the wings are SHALLOW and long, not a deep V,
 *  - each one CRANKS at the wrist: it rises from the body, peaks about two
 *    thirds out, and the tip comes back DOWN. A wing that only rises is a bat's.
 *  - the body is slim and inline, not a bead hanging below the join.
 *
 * The wing is also a blade of its own rather than part of one closed outline: a
 * single silhouette cannot be thick at the shoulder AND pointed at the tip, and
 * the even, blunt result was what the owner first called "alelade".
 */
export function gullD( o: GullShape ): string {
  const cx = 60, cy = o.cy ?? 60;
  const asym = o.asym ?? 0;
  const rootW = o.rootW ?? 4.3;
  const taper = o.taper ?? 1.15;
  const spanBase = o.span ?? 54;
  const c1x = o.c1x ?? 20, c2x = o.c2x ?? 40;
  const rootIn = o.rootIn ?? 5, tipF = o.tipF ?? 0.62;
  const bodyW = o.bodyW ?? 11, belly = o.belly ?? 5.4, back = o.back ?? 4;
  const rise = o.rise ?? ( 6 + ( o.lift ?? 0 ) * 15 );
  const wing = ( dir: number, extra: number ): string => {
    const r = rise * ( 1 + extra * 0.16 );
    const span = spanBase + extra * 4;
    return blade(
      // Root set PAST the centre, so the two wings overlap and their undersides
      // merge rather than butting together.
      [ cx - dir * rootIn, cy + 0.5 ],
      [ cx + dir * c1x, cy - r * 0.95 ],
      [ cx + dir * c2x, cy - r * 1.12 ],
      [ cx + dir * span, cy - r * tipF ],   // the tip comes back down: the crank
      rootW, 24, taper );
  };
  // Slim and inline, and wide + deep enough to swallow both wing roots so the
  // belly reads as one curve.
  const body = `M${ cx - bodyW },${ cy - 0.8 } Q${ cx },${ cy + belly } ${ cx + bodyW },${ cy - 0.8 }`
    + ` Q${ cx },${ cy - back } ${ cx - bodyW },${ cy - 0.8 } Z`;
  return `${ wing( -1, 0 ) } ${ wing( 1, asym ) } ${ body }`;
}

/** The WALLPAPER's gull: `gullD` on every default, which is what this function
 *  has always drawn. Kept as its own name because the flock calls it per beat. */
export function birdD( lift: number, asym = 0 ): string {
  return gullD( { lift, asym } );
}

/** Eight birds: four points in the wing beat, each in a left- and a right-leaning
 *  asymmetry, so a flock never shows the same silhouette twice nearby.
 *  reduce/concat rather than flatMap: this project's TS lib target predates it. */
export const BIRDS: string[] = [ 0.25, 0.5, 0.75, 1 ].reduce(
  ( out, l ) => out.concat( [ birdD( l, 0.6 ), birdD( l, -0.5 ) ] ), [] as string[] );

/**
 * The flock's colours: the theme's OWN sunset palette rather than tints of the
 * chat skin (owner: "roraima renklerinde"). Mixed browns close to the skin were
 * the first try and the birds all but disappeared into it.
 */
export const BIRD_COLORS: string[] = [
  '#D17458',   // Coral Canyon
  '#DDBE6D',   // Dry Grass
  '#ED8B8B',   // Pink Quartz
  '#D69568',   // Amber Sand
  '#CC654E',   // Sunset Mesa
  '#AD8A8A',   // Misty Rose
  '#8F78B7',   // Orchid Mist, one twilight note in the flock
];

/**
 * ISTANBUL'S PASSCODE GULL (owner 2026-08-14: the dots are martılar).
 *
 * The same bird as the flock above, at the same WEIGHT as the flock — see the
 * note on the numbers below, which is the owner's own correction — and simply
 * drawn big enough to fill the tile alone.
 *
 * The BEAT is the one thing tuned for this use. Set high (rise 32) the wings
 * drop steeply into the roots and the centre fuses into a single cup at 22px:
 * bold, but no longer a bird. At 30, over a slim body, the dip stays shallow
 * enough that two wings and two upswept tips still read, which is the entire
 * seagull cue at that size.
 *
 * ONE DRAWING, ONE COLOUR PER STATE — the rule the owner set on Sakura and
 * repeated on Roraima's sun ("hepsi 3.sü olsun sadece renkleri farklı olsun").
 * No per-digit tilt or pose here: a version where the gull changed per digit
 * was rejected.
 */
export const GULL_PASSCODE: string = gullD( {
  // The wallpaper's OWN weight, deliberately (owner 2026-08-14: "passcode
  // martıları çok kalın olmuş, chat backgrounddaki gibi olabilirler"). A first
  // pass fattened the bird for the 22px gate — rootW 12 against the flock's 4.3,
  // taper 0.80 against 1.15 — and the result read as a heavy blunt wedge rather
  // than as the same gull the chat screen is full of. Two screens of one theme
  // showing two different birds is exactly what the shared module exists to
  // prevent, so the weight follows the flock and only the SIZE differs: the
  // passcode draws one gull filling the box, the wallpaper scatters it small.
  rise: 30, span: 54, c1x: 20, c2x: 40, rootW: 4.6, taper: 1.15,
  bodyW: 11, belly: 5.0, back: 3.8, cy: 68,
} );

/**
 * The passcode run: entered gulls are WHITE, unentered ones a pale blue
 * (owner 2026-08-14: "passcodeda OFF yani içi boş olanlar soluk mavi, içi dolu
 * olanlar beyaz olsun").
 *
 * This replaces a first pass that ran the four ON dots through the theme's DARK
 * half — Midnight Strait, Deep Bosphorus and the two Rooftop Reds — on the
 * reasoning that the upper zone's cyan is light (relative luminance 0.459) and
 * only dark ink measures well on it. It measured fine and looked wrong: four
 * different dark birds on a bright cyan field read as four unrelated marks, and
 * a passcode row is not a place for four colours. White is also what LINE's own
 * passcode draws on this zone, and what a seagull actually is.
 *
 * All four ON dots share the one colour, so the row differs only by how many are
 * filled — which is the whole job of a passcode row.
 *
 * Contrast against the #4AC3E0 upper zone:
 *   ON  #FFFFFF   2.06:1   the loudest thing the zone allows
 *   OFF #A3DDE5   1.38:1   Sea Glass, plainly a pale BLUE next to the white
 * and 1.50:1 between the two states, so a digit landing always reads as the gull
 * brightening. Bosphorus Mist #DBF0F1 was the other candidate for OFF and was
 * dropped at 1.19:1 against the white — too near it to read as a second state.
 */
export const GULL_PC_COLORS: string[] = [ '#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF' ];

/**
 * ONE tone for every unentered gull — the frame never changes per digit (the
 * rule Aurora's orbs settled). A flat fill and not an outline, for Miami's
 * reason: an outline of this silhouette is two hairlines at 22px.
 */
export const GULL_PC_OFF = '#A3DDE5';
